import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Euro, TrendingUp, ShoppingCart, Award, Tag, Copy, Check, BarChart3, ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';

const StatCard = ({ icon, label, value, sub, color = '#FF6B35', delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40 hover:border-[#FF6B35]/30 transition-all" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-[#6B6B6E] text-xs font-semibold uppercase tracking-wider">{label}</span>
      <div style={{ color }} className="opacity-60">{icon}</div>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {sub && <p className="text-[#6B6B6E] text-xs mt-1">{sub}</p>}
  </motion.div>
);

export default function InfluencerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/influencer/dashboard');
      setData(res.data);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-64 bg-[#2A2A2D] rounded" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-[#2A2A2D] rounded-lg" />)}
          </div>
          <div className="h-48 bg-[#2A2A2D] rounded-lg" />
        </div>
      </div>
    );
  }

  const { influencer, promo_codes, stats } = data || {};

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="influencer-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white">
          Bonjour, <span className="text-[#FF6B35]">{influencer?.pseudo || user?.full_name}</span>
        </h1>
        <p className="text-[#6B6B6E] text-sm mt-1">Votre espace influenceur</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<Euro className="h-5 w-5" />} label="Solde disponible" value={`${(stats?.balance_available || 0).toFixed(2)}€`} sub="Retirable" color="#00FF88" delay={0} />
        <StatCard icon={<TrendingUp className="h-5 w-5" />} label="Total commissions" value={`${(stats?.total_commission || 0).toFixed(2)}€`} sub="Depuis le début" color="#00FFFF" delay={0.05} />
        <StatCard icon={<ShoppingCart className="h-5 w-5" />} label="Ventes générées" value={stats?.total_sales || 0} sub={`${(stats?.total_amount || 0).toFixed(2)}€ de CA`} color="#FF6B35" delay={0.1} />
        <StatCard icon={<Award className="h-5 w-5" />} label="Classement" value={`#${stats?.ranking || '-'}`} sub={`sur ${stats?.total_influencers || 0} influenceurs`} color="#FFB800" delay={0.15} />
      </div>

      {/* Promo Codes */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]/40" data-testid="promo-codes-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Tag className="h-5 w-5 text-[#FF6B35]" />Vos codes promo</h2>
        </div>
        {promo_codes?.length > 0 ? (
          <div className="space-y-3">
            {promo_codes.map((pc, i) => (
              <div key={i} className="bg-[#222226] p-4 rounded-lg border border-[#3A3A3D]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`promo-code-${pc.code}`}>
                <div className="flex items-center gap-3">
                  <span className="text-[#FF6B35] font-mono font-bold text-lg">{pc.code}</span>
                  <button onClick={() => copyText(pc.code, pc.code)} className="text-[#6B6B6E] hover:text-[#FF6B35] transition-colors">
                    {copied === pc.code ? <Check className="h-4 w-4 text-[#00FF88]" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex items-center gap-4 text-xs text-[#B8B8BA]">
                  <span>Réduction : <strong className="text-[#00FF88]">{pc.discount_euros}€</strong></span>
                  <span>Commission : <strong className="text-[#00FFFF]">{pc.commission_euros}€</strong></span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#6B6B6E] text-sm">Aucun code promo attribué. Contactez l'administrateur.</p>
        )}
      </motion.div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/influencer/stats" className="bg-[#1A1A1D] p-4 rounded-lg border border-[#3A3A3D]/40 hover:border-[#FF6B35]/30 transition-all flex items-center justify-between group" data-testid="link-stats">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-[#00FFFF]" />
            <span className="text-white text-sm font-medium">Voir les statistiques</span>
          </div>
          <ArrowUpRight className="h-4 w-4 text-[#6B6B6E] group-hover:text-[#FF6B35] transition-colors" />
        </Link>
        <Link to="/influencer/payouts" className="bg-[#1A1A1D] p-4 rounded-lg border border-[#3A3A3D]/40 hover:border-[#FF6B35]/30 transition-all flex items-center justify-between group" data-testid="link-payouts">
          <div className="flex items-center gap-3">
            <Euro className="h-5 w-5 text-[#00FF88]" />
            <span className="text-white text-sm font-medium">Demander un virement</span>
          </div>
          <ArrowUpRight className="h-4 w-4 text-[#6B6B6E] group-hover:text-[#FF6B35] transition-colors" />
        </Link>
        <Link to="/influencer/profile" className="bg-[#1A1A1D] p-4 rounded-lg border border-[#3A3A3D]/40 hover:border-[#FF6B35]/30 transition-all flex items-center justify-between group" data-testid="link-profile">
          <div className="flex items-center gap-3">
            <Award className="h-5 w-5 text-[#FFB800]" />
            <span className="text-white text-sm font-medium">Mon profil</span>
          </div>
          <ArrowUpRight className="h-4 w-4 text-[#6B6B6E] group-hover:text-[#FF6B35] transition-colors" />
        </Link>
      </div>

      {/* Recent Sales */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]/40" data-testid="recent-sales">
        <h2 className="text-lg font-bold text-white mb-4">Dernières ventes</h2>
        <RecentSales />
      </motion.div>
    </div>
  );
}

function RecentSales() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/influencer/sales?limit=5').then(r => setSales(r.data.sales)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse h-20 bg-[#2A2A2D] rounded" />;
  if (!sales.length) return <p className="text-[#6B6B6E] text-sm">Aucune vente pour le moment</p>;

  return (
    <div className="space-y-2">
      {sales.map((s, i) => (
        <div key={i} className="flex items-center justify-between py-2 border-b border-[#3A3A3D]/30 last:border-0">
          <div>
            <p className="text-white text-sm">{s.client_email}</p>
            <p className="text-[#6B6B6E] text-xs">{new Date(s.created_at).toLocaleDateString('fr-FR')}</p>
          </div>
          <div className="text-right">
            <p className="text-[#00FF88] font-bold text-sm">+{s.commission?.toFixed(2)}€</p>
            <p className="text-[#6B6B6E] text-xs">sur {s.amount?.toFixed(2)}€</p>
          </div>
        </div>
      ))}
    </div>
  );
}
